import { useState, useEffect, useCallback, useRef } from 'react'
import { useAccount, useReadContract, useWriteContract, usePublicClient, useChainId, useSwitchChain } from 'wagmi'
import { parseEther, formatEther, parseAbi } from 'viem'
import { useToast } from '../context/ToastContext'
import { Icon } from '../components/Layout'
import { updateProfile } from '../services/profiles'

// ─── Contract config ──────────────────────────────────────────
const STAKING_ADDRESS = import.meta.env.VITE_STAKING_CONTRACT_ADDRESS
const CLT_ADDRESS     = import.meta.env.VITE_CLT_CONTRACT_ADDRESS
const SEPOLIA_ID      = 11155111

// ─── Wrong network banner ─────────────────────────────────────
function WrongNetworkBanner() {
  const { switchChain, isPending } = useSwitchChain()
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-on-background/70 p-4">
      <div className="bg-surface border-4 border-on-background w-full max-w-sm flex flex-col gap-5 p-6 text-center"
        style={{ boxShadow: '6px 6px 0px 0px var(--neo-border-color)' }}>
        <span className="material-symbols-outlined text-[40px] text-error mx-auto">wrong_location</span>
        <div>
          <p className="font-extrabold text-[15px] uppercase tracking-widest">Wrong Network</p>
          <p className="text-[13px] text-on-surface-variant mt-1">Chicago Staking runs on <strong>Ethereum Sepolia</strong>. Switch your wallet to continue.</p>
        </div>
        <button onClick={() => switchChain({ chainId: SEPOLIA_ID })} disabled={isPending}
          className="py-3 bg-on-background text-surface font-bold text-[12px] uppercase tracking-widest hover:opacity-90 disabled:opacity-50 transition-opacity border-2 border-on-background"
          style={{ boxShadow: '3px 3px 0px 0px var(--neo-border-color)' }}>
          {isPending ? 'Switching…' : 'Switch to Sepolia'}
        </button>
      </div>
    </div>
  )
}

const STAKING_ABI = parseAbi([
  'function stake(uint256 amount, uint256 duration) external',
  'function withdraw(uint256 stakeIndex) external',
  'function withdrawAllMatured() external returns (uint256)',
  'function getActiveStakes(address staker) external view returns ((uint128 amount, uint64 startTime, uint64 endTime, bool withdrawn)[] active, uint256[] indices)',
  'function globalTotalStaked() external view returns (uint256)',
  'function totalStaked(address) external view returns (uint256)',
  'function timeRemaining(address staker, uint256 index) external view returns (uint256)',
  'function minStakeAmount() external view returns (uint256)',
  'function validDurations() external view returns (uint256[4])',
])

const CLT_ABI = parseAbi([
  'function balanceOf(address owner) external view returns (uint256)',
  'function allowance(address owner, address spender) external view returns (uint256)',
  'function approve(address spender, uint256 amount) external returns (bool)',
])

// ─── Lock duration config ──────────────────────────────────────
const DAY = 24 * 60 * 60

const LOCK_OPTIONS = [
  { label: '90 Days',  seconds: 90  * DAY, desc: '3 months',  multiplier: 1.0  },
  { label: '180 Days', seconds: 180 * DAY, desc: '6 months',  multiplier: 1.25 },
  { label: '360 Days', seconds: 360 * DAY, desc: '1 year',    multiplier: 1.6  },
  { label: '540 Days', seconds: 540 * DAY, desc: '18 months', multiplier: 2.0  },
]

// CIS Boost = Staked CLT × Duration Multiplier × Maturity Bonus (0.5x on
// day 1, ramping linearly to 1.0x once the lock term fully matures) ×
// stakingFactor (0.2) — see cis.service.ts calculateLockScore/recalculate.
// minCis/maxCis below show that day-1 → maturity range for a 3,000 CLT
// stake at each duration, so the table doesn't imply a single flat value.
const EXAMPLE_STAKE_CLT = 3000
const STAKING_FACTOR = 0.2
const CIS_BOOST_MULTIPLIERS = [
  { label: '90 Days', multiplier: '1.0x', minCis: 300, maxCis: 600, icon: 'bolt' },
  { label: '180 Days', multiplier: '1.25x', minCis: 375, maxCis: 750, icon: 'bolt' },
  { label: '360 Days', multiplier: '1.6x', minCis: 480, maxCis: 960, icon: 'local_fire_department' },
  { label: '540 Days', multiplier: '2.0x', minCis: 600, maxCis: 1200, icon: 'local_fire_department' },
]

// NOTE: these are display percentages for a 70/20/10 split, matching the
// same split already used in Leaderboard.jsx (SOCIAL_MAX/STAKING_MAX/
// CONSISTENCY_MAX = 70k/20k/10k). The real backend formula isn't actually
// a normalized percentage split — it's literal per-action points with an
// admin-configurable weight per component (default 1.0x each, see
// cis.service.ts + setting.service.ts's getCisWeights) — so treat this as
// an illustrative approximation, not an exact mirror.
const SCORE_WEIGHTS = [
  { label: 'Social Reputation', value: 70, icon: 'groups',  desc: 'Likes, posts & comments earned' },
  { label: 'Staking',           value: 20, icon: 'lock',    desc: 'CLT staked, scaled by lock duration' },
  { label: 'Consistency',       value: 10, icon: 'repeat',  desc: 'Daily activity streak' },
]

// ─── Helpers ──────────────────────────────────────────────────
function fmt(wei)    { return wei != null ? Number(formatEther(wei)).toLocaleString(undefined, { maximumFractionDigits: 2 }) : '—' }
function fmtSecs(s)  {
  if (!s || s <= 0) return 'Matured'
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600)
  return d > 0 ? `${d}d ${h}h` : `${h}h`
}
function lockLabel(endTime) {
  const now = Math.floor(Date.now() / 1000)
  const remaining = Number(endTime) - now
  return remaining > 0 ? fmtSecs(remaining) : 'Matured ✓'
}
function isMatured(endTime) {
  return Number(endTime) <= Math.floor(Date.now() / 1000)
}
function pct(amount, total) {
  if (!total || total === 0n) return 0
  return Math.min(100, Math.round(Number((amount * 10000n) / total) / 100))
}

// ─── Contract not deployed yet — construction preloader ───────
function NotDeployedBanner() {
  const [dots, setDots] = useState(0)
  const [progress, setProgress] = useState(17)

  useEffect(() => {
    const dotsT = setInterval(() => setDots(d => (d + 1) % 4), 500)
    const progT = setInterval(() => setProgress(p => {
      const next = p + Math.random() * 2.5
      return next >= 94 ? 94 : next  // stall at 94% — not deployed yet 😅
    }), 800)
    return () => { clearInterval(dotsT); clearInterval(progT) }
  }, [])

  if (STAKING_ADDRESS) return null

  return (
    <div
      className="border-4 border-on-background bg-on-background text-surface p-6 lg:p-8 flex flex-col gap-5 overflow-hidden relative"
      style={{ boxShadow: '4px 4px 0px 0px var(--neo-shadow-color)' }}
    >
      {/* animated diagonal stripe bg */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage: 'repeating-linear-gradient(45deg, currentColor 0, currentColor 1px, transparent 0, transparent 50%)',
          backgroundSize: '12px 12px',
        }}
      />

      {/* top row */}
      <div className="flex items-center gap-3 relative z-10">
        <div className="w-10 h-10 border-2 border-primary-container flex items-center justify-center flex-shrink-0">
          <span
            className="material-symbols-outlined text-[22px] text-primary-container"
            style={{ animation: 'spin 2s linear infinite' }}
          >
            settings
          </span>
        </div>
        <div>
          <p className="font-extrabold text-[13px] uppercase tracking-[0.18em] text-primary-container">
            Under Construction
          </p>
          <p className="text-[11px] font-bold uppercase tracking-widest opacity-50 mt-0.5">
            Contract deploying{'.'.repeat(dots)}
          </p>
        </div>
        <div className="ml-auto text-right">
          <p className="font-extrabold text-2xl leading-none text-primary-container">
            {Math.floor(progress)}%
          </p>
          <p className="text-[9px] uppercase tracking-widest opacity-40 mt-0.5">complete</p>
        </div>
      </div>

      {/* progress bar */}
      <div className="relative z-10">
        <div className="h-3 border-2 border-surface/20 overflow-hidden bg-surface/10">
          <div
            className="h-full bg-primary-container transition-all duration-700 ease-out relative"
            style={{ width: `${progress}%` }}
          >
            {/* shimmer */}
            <div
              className="absolute inset-0 opacity-40"
              style={{
                background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.6) 50%, transparent 100%)',
                animation: 'shimmer 1.8s ease-in-out infinite',
              }}
            />
          </div>
        </div>
        <style>{`
          @keyframes shimmer { 0%{transform:translateX(-100%)} 100%{transform:translateX(400%)} }
          @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        `}</style>
      </div>

      {/* tasks checklist */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 relative z-10 text-[11px] font-bold uppercase tracking-widest">
        {[
          { label: 'Contract written',  done: true  },
          { label: 'Audit & deploy',    done: false },
        ].map(task => (
          <div key={task.label} className={`flex items-center gap-2 border px-3 py-2 ${task.done ? 'border-primary-container/40 text-primary-container' : 'border-surface/20 opacity-40'}`}>
            <span className="material-symbols-outlined text-[14px]">
              {task.done ? 'check_circle' : 'radio_button_unchecked'}
            </span>
            {task.label}
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Stake card ────────────────────────────────────────────────
function StakeCard({ stake, index, globalTotal, onWithdraw, withdrawing }) {
  const { address } = useAccount()
  const matured = isMatured(stake.endTime)

  const share = pct(BigInt(stake.amount), globalTotal)
  const duration = Number(stake.endTime) - Number(stake.startTime)
  const lockOpt = LOCK_OPTIONS.find(o => o.seconds === duration)

  return (
    <div className={`border-2 p-4 flex flex-col gap-3 ${matured ? 'border-primary-container bg-primary-container/5' : 'border-on-background/20 bg-surface-container'}`}>
      {/* Amount + lock */}
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-extrabold text-xl leading-none text-on-surface">{fmt(BigInt(stake.amount))} CLT</p>
          <p className="text-[11px] font-bold uppercase tracking-widest text-on-surface-variant mt-1">
            {lockOpt?.label ?? `${Math.round(duration / 86400)}d lock`}
          </p>
        </div>
        <span className={`text-[10px] font-extrabold uppercase tracking-widest px-2 py-1 border ${matured ? 'border-primary-container text-primary-container bg-primary-container/10' : 'border-on-background/20 text-on-surface-variant'}`}>
          {matured ? 'Ready' : 'Locked'}
        </span>
      </div>

      {/* Progress bar */}
      <div>
        <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest text-on-surface-variant mb-1">
          <span>{lockLabel(stake.endTime)}</span>
          <span>{share}% of pool</span>
        </div>
        <div className="h-1.5 bg-surface-container-high border border-on-background/10 overflow-hidden">
          <div
            className="h-full bg-primary-container transition-all"
            style={{ width: `${share}%` }}
          />
        </div>
      </div>

      {/* Locked notice */}
      {!matured && (
        <p className="text-[11px] text-on-surface-variant font-bold flex items-center gap-1">
          <span className="material-symbols-outlined text-[13px]">lock</span>
          Fully locked until maturity — no early withdrawal
        </p>
      )}

      {/* Withdraw button */}
      <button
        onClick={() => onWithdraw(index)}
        disabled={!matured || withdrawing || !STAKING_ADDRESS}
        className={`w-full border-2 py-2 text-[11px] font-extrabold uppercase tracking-widest transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
          matured
            ? 'border-primary-container bg-primary-container text-on-primary-fixed hover:bg-primary'
            : 'border-on-background/20 text-on-surface-variant cursor-not-allowed'
        }`}
      >
        {withdrawing ? 'Withdrawing...' : matured ? 'Withdraw' : `Locked — ${lockLabel(stake.endTime)}`}
      </button>
    </div>
  )
}

// ─── Main page ─────────────────────────────────────────────────
export default function Staking() {
  const { address, isConnected } = useAccount()
  const chainId = useChainId()
  const { switchChain } = useSwitchChain()
  const toast = useToast()

  const isWrongNetwork = isConnected && chainId !== SEPOLIA_ID

  const [amount, setAmount]         = useState('')
  const [duration, setDuration]     = useState(LOCK_OPTIONS[0].seconds)
  const [approving, setApproving]   = useState(false)
  const [staking, setStaking]       = useState(false)
  const [withdrawing, setWithdrawing] = useState(null) // stakeIndex | null

  // Connecting a wallet here (for signing stake/withdraw txs) never used to
  // tell the backend this wallet is yours — so the staking-sync job had no
  // way to resolve your on-chain stakes back to your profile ("no profile
  // has this wallet connected"). Link it as soon as it's connected, once.
  const linkedWalletRef = useRef(null)
  useEffect(() => {
    if (!isConnected || !address) return
    if (linkedWalletRef.current === address) return
    linkedWalletRef.current = address
    updateProfile(null, { walletAddress: address }).catch(() => {
      // Non-fatal — worst case the staking-sync job logs a mismatch again
      // and it's visible in the backend logs, same as before this fix.
      linkedWalletRef.current = null
    })
  }, [isConnected, address])

  // ── Read contract state ──
  const { data: globalTotal, refetch: refetchGlobal } = useReadContract({
    address: STAKING_ADDRESS,
    abi: STAKING_ABI,
    functionName: 'globalTotalStaked',
    enabled: !!STAKING_ADDRESS,
  })

  const { data: userTotal, refetch: refetchUserTotal } = useReadContract({
    address: STAKING_ADDRESS,
    abi: STAKING_ABI,
    functionName: 'totalStaked',
    args: [address],
    enabled: !!STAKING_ADDRESS && !!address,
  })

  const { data: stakesData, refetch: refetchStakes } = useReadContract({
    address: STAKING_ADDRESS,
    abi: STAKING_ABI,
    functionName: 'getActiveStakes',
    args: [address],
    enabled: !!STAKING_ADDRESS && !!address,
  })

  const { data: cltBalance, refetch: refetchBalance } = useReadContract({
    address: CLT_ADDRESS,
    abi: CLT_ABI,
    functionName: 'balanceOf',
    args: [address],
    enabled: !!address,
  })

  const { data: allowance, refetch: refetchAllowance } = useReadContract({
    address: CLT_ADDRESS,
    abi: CLT_ABI,
    functionName: 'allowance',
    args: [address, STAKING_ADDRESS],
    enabled: !!address && !!STAKING_ADDRESS,
  })

  const { writeContractAsync } = useWriteContract()
  const publicClient = usePublicClient()

  // Wait for a tx to actually be mined before trusting on-chain reads again
  const waitForConfirmation = useCallback(async (hash) => {
    if (!publicClient || !hash) return
    await publicClient.waitForTransactionReceipt({ hash })
  }, [publicClient])

  const refetchAll = useCallback(() => {
    refetchGlobal(); refetchUserTotal(); refetchStakes()
    refetchBalance(); refetchAllowance()
  }, [refetchGlobal, refetchUserTotal, refetchStakes, refetchBalance, refetchAllowance])

  const activeStakes = stakesData?.[0] ?? []
  const stakeIndices = stakesData?.[1] ?? []
  const amountWei    = amount ? parseEther(amount) : 0n
  const needsApproval = allowance != null && amountWei > 0n && allowance < amountWei
  const selectedLock = LOCK_OPTIONS.find(opt => opt.seconds === duration)
  const projectedCisBoost = amount && !Number.isNaN(Number(amount))
    ? Number(amount) * (selectedLock?.multiplier ?? 1)
    : 0

  const maturedCount = activeStakes.filter(s => isMatured(s.endTime)).length

  // ── Approve ──
  const handleApprove = async () => {
    if (!STAKING_ADDRESS) return
    if (chainId !== SEPOLIA_ID) {
      try { await switchChain({ chainId: SEPOLIA_ID }) } catch {}
      return
    }
    setApproving(true)
    try {
      const hash = await writeContractAsync({
        address: CLT_ADDRESS,
        abi: CLT_ABI,
        functionName: 'approve',
        args: [STAKING_ADDRESS, amountWei],
      })
      await waitForConfirmation(hash)
      toast.success('Approved! Now stake your CLT.')
      await refetchAllowance()
    } catch (e) {
      toast.error(e.shortMessage ?? e.message ?? 'Approval failed')
    } finally {
      setApproving(false)
    }
  }

  // ── Stake ──
  const handleStake = async () => {
    if (!STAKING_ADDRESS || !amount || amountWei <= 0n) return
    setStaking(true)
    try {
      const hash = await writeContractAsync({
        address: STAKING_ADDRESS,
        abi: STAKING_ABI,
        functionName: 'stake',
        args: [amountWei, BigInt(duration)],
      })
      await waitForConfirmation(hash) // block until the stake is actually mined
      toast.success('Staked successfully!')
      setAmount('')
      refetchAll()
    } catch (e) {
      toast.error(e.shortMessage ?? e.message ?? 'Stake failed')
    } finally {
      setStaking(false)
    }
  }

  // ── Withdraw single (matured only — no early exit) ──
  const handleWithdraw = async (stakeIndex) => {
    if (!STAKING_ADDRESS) return
    setWithdrawing(stakeIndex)
    try {
      const hash = await writeContractAsync({
        address: STAKING_ADDRESS,
        abi: STAKING_ABI,
        functionName: 'withdraw',
        args: [BigInt(stakeIndex)],
      })
      await waitForConfirmation(hash)
      toast.success('Withdrawn!')
      refetchAll()
    } catch (e) {
      toast.error(e.shortMessage ?? e.message ?? 'Withdraw failed')
    } finally {
      setWithdrawing(null)
    }
  }

  // ── Withdraw all matured ──
  const handleWithdrawAllMatured = async () => {
    if (!STAKING_ADDRESS) return
    setWithdrawing('all')
    try {
      const hash = await writeContractAsync({
        address: STAKING_ADDRESS,
        abi: STAKING_ABI,
        functionName: 'withdrawAllMatured',
      })
      await waitForConfirmation(hash)
      toast.success('All matured stakes withdrawn!')
      refetchAll()
    } catch (e) {
      toast.error(e.shortMessage ?? e.message ?? 'Withdraw failed')
    } finally {
      setWithdrawing(null)
    }
  }

  // ── Determine button state ──
  const canStake = isConnected && STAKING_ADDRESS && amount && amountWei > 0n
  const buttonLabel = !isConnected
    ? 'Connect Wallet to Stake'
    : !STAKING_ADDRESS
    ? 'Contract Not Deployed Yet'
    : !amount || amountWei <= 0n
    ? 'Enter an Amount'
    : approving ? 'Approving...'
    : staking   ? 'Staking...'
    : needsApproval ? 'Approve CLT'
    : 'Stake CLT'

  return (
    <div className="flex-1 lg:ml-[300px] w-full max-w-4xl flex flex-col gap-4 lg:gap-6">

      {isWrongNetwork && <WrongNetworkBanner />}

      {/* Header */}
      <section className="border border-on-background/10 lg:neo-border lg:neo-shadow bg-surface-container p-4 lg:p-8">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary-container mb-1">CLT Staking</p>
        <h1 className="text-[1.6rem] lg:text-display-lg font-extrabold uppercase tracking-tight leading-none mb-2">
          Stake. Earn. Rank Up.
        </h1>
        <p className="text-sm text-on-surface-variant max-w-xl">
          Lock CLT tokens directly in the Chicago staking contract. Longer locks earn higher CIS multipliers and push you up the leaderboard.
        </p>
      </section>

      <NotDeployedBanner />

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {[
          { label: 'Global TVL',    value: globalTotal != null ? `${fmt(globalTotal)} CLT` : '—' },
          { label: 'Your Staked',   value: userTotal   != null ? `${fmt(userTotal)} CLT`   : isConnected ? '0 CLT' : '—' },
          { label: 'Your Balance',  value: cltBalance  != null ? `${fmt(cltBalance)} CLT`  : isConnected ? '—' : '—' },
        ].map(stat => (
          <div key={stat.label} className="border-2 border-on-background/20 p-3 bg-surface-container flex flex-col gap-1">
            <p className="font-extrabold text-lg lg:text-2xl leading-none text-on-surface">{stat.value}</p>
            <p className="text-[10px] uppercase font-bold tracking-widest text-on-surface-variant">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Stake form — only if wallet connected */}
      {isConnected && (
        <section
          className="bg-surface border-4 border-on-background p-5 lg:p-6"
          style={{ boxShadow: '4px 4px 0px 0px var(--neo-shadow-color)' }}
        >
          <h2 className="font-bold text-[11px] uppercase tracking-[0.14em] mb-5 pb-3 border-b-[3px] border-on-background text-on-surface">
            New Stake
          </h2>

          {/* Amount input */}
          <div className="flex flex-col gap-1 mb-4">
            <label className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">Amount (CLT)</label>
            <div className="flex gap-2">
              <input
                type="number"
                min="1"
                step="1"
                placeholder="0.00"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                className="flex-1 bg-surface-container border-2 border-on-background/30 focus:border-primary-container outline-none px-3 py-2 text-on-surface font-bold text-base"
              />
              {cltBalance != null && (
                <button
                  onClick={() => setAmount(formatEther(cltBalance))}
                  className="px-3 py-2 border-2 border-on-background/20 text-[10px] font-extrabold uppercase tracking-widest text-on-surface-variant hover:border-primary-container hover:text-primary-container transition-colors"
                >
                  Max
                </button>
              )}
            </div>
            {cltBalance != null && (
              <p className="text-[10px] text-on-surface-variant">Balance: {fmt(cltBalance)} CLT</p>
            )}
          </div>

          {/* Duration selector */}
          <div className="flex flex-col gap-1 mb-5">
            <label className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">Lock Duration</label>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
              {LOCK_OPTIONS.map(opt => (
                <button
                  key={opt.seconds}
                  onClick={() => setDuration(opt.seconds)}
                  className={`border-2 py-3 px-2 flex flex-col items-center gap-0.5 transition-colors ${
                    duration === opt.seconds
                      ? 'border-primary-container bg-primary-container text-on-primary-fixed'
                      : 'border-on-background/20 text-on-surface-variant hover:border-primary-container hover:text-on-surface'
                  }`}
                >
                  <span className="text-[12px] font-extrabold uppercase tracking-wide">{opt.label}</span>
                  <span className="text-[9px] uppercase tracking-widest opacity-60">{opt.desc}</span>
                </button>
              ))}
            </div>
            <p className="text-[10px] text-on-surface-variant mt-1 flex items-center gap-1">
              <span className="material-symbols-outlined text-[12px]">lock</span>
              Selected: <span className="text-primary-container font-bold">{selectedLock?.label}</span>
              {' · '}Stakes are fully locked until expiry
            </p>
            <div className="mt-2 border-2 border-primary-container/30 bg-primary-container/5 p-3 flex items-center justify-between gap-3">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">Projected CIS Boost</p>
                <p className="text-[11px] text-on-surface-variant mt-1">
                  {amount && Number(amount) > 0
                    ? `${projectedCisBoost.toLocaleString(undefined, { maximumFractionDigits: 2 })} CIS`
                    : 'Enter an amount to preview'}
                </p>
              </div>
              <p className="font-extrabold text-base text-primary-container">
                {selectedLock?.multiplier ?? 1}×
              </p>
            </div>
          </div>

          {/* Approve / Stake button */}
          <button
            onClick={needsApproval ? handleApprove : handleStake}
            disabled={!canStake || approving || staking}
            className="w-full border-4 border-on-background bg-on-background text-surface py-3 font-extrabold text-[13px] uppercase tracking-widest hover:bg-primary-container hover:border-primary-container hover:text-on-primary-fixed transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ boxShadow: '3px 3px 0px 0px var(--neo-shadow-color)' }}
          >
            {buttonLabel}
          </button>
        </section>
      )}

      {/* My active stakes */}
      {isConnected && activeStakes.length > 0 && (
        <section
          className="bg-surface border-4 border-on-background p-5 lg:p-6"
          style={{ boxShadow: '4px 4px 0px 0px rgba(212,175,55,1)' }}
        >
          <div className="flex items-center justify-between mb-5 pb-3 border-b-[3px] border-on-background">
            <h2 className="font-bold text-[11px] uppercase tracking-[0.14em] text-on-surface">
              Your Stakes ({activeStakes.length})
            </h2>
            {maturedCount > 1 && (
              <button
                onClick={handleWithdrawAllMatured}
                disabled={withdrawing === 'all' || !STAKING_ADDRESS}
                className="border-2 border-primary-container text-primary-container px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-widest hover:bg-primary-container hover:text-on-primary-fixed transition-colors disabled:opacity-40"
              >
                {withdrawing === 'all' ? 'Withdrawing...' : `Withdraw All Matured (${maturedCount})`}
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {activeStakes.map((stake, i) => (
              <StakeCard
                key={stakeIndices[i]?.toString() ?? i}
                stake={stake}
                index={Number(stakeIndices[i])}
                globalTotal={globalTotal ?? 0n}
                onWithdraw={handleWithdraw}
                withdrawing={withdrawing === Number(stakeIndices[i])}
              />
            ))}
          </div>
        </section>
      )}

      {/* Connect wallet prompt */}
      {!isConnected && (
        <section className="border-4 border-on-background/20 border-dashed p-8 flex flex-col items-center gap-3 text-center">
          <Icon name="account_balance_wallet" className="text-[40px] text-on-surface-variant/40" />
          <p className="font-extrabold text-[13px] uppercase tracking-widest text-on-surface-variant">Connect your wallet to stake</p>
          <p className="text-[12px] text-on-surface-variant/60 max-w-xs">Your stakes, balance, and rewards will appear here once connected.</p>
        </section>
      )}

      {/* How score is calculated */}
      <section
        className="bg-surface border-4 border-on-background p-5 lg:p-6"
        style={{ boxShadow: '4px 4px 0px 0px var(--neo-shadow-color)' }}
      >
        <h2 className="font-bold text-[11px] uppercase tracking-[0.14em] mb-5 pb-3 border-b-[3px] border-on-background text-on-surface">
          How CIS Is Calculated
        </h2>
        <div className="flex flex-col gap-3">
          {SCORE_WEIGHTS.map((w) => (
            <div key={w.label} className="flex items-center gap-4">
              <div className="w-12 h-12 flex-shrink-0 bg-on-background text-surface flex items-center justify-center border-2 border-on-background font-extrabold text-[11px] uppercase">
                {w.value}%
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-bold text-[13px] uppercase tracking-wide text-on-surface">{w.label}</span>
                  <span className="font-extrabold text-primary-container text-base">{w.value}%</span>
                </div>
                <div className="h-2 bg-surface-container border border-on-background/20 overflow-hidden">
                  <div className="h-full bg-primary-container transition-all" style={{ width: `${w.value}%` }} />
                </div>
                <p className="text-[11px] text-on-surface-variant mt-1">{w.desc}</p>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-3 text-[11px] text-on-surface-variant">
          Staking power is not shown separately here. Your lock duration boost is displayed below.
        </p>
      </section>

      {/* CIS Boost preview */}
      <section
        className="bg-surface border-4 border-on-background p-5 lg:p-6"
        style={{ boxShadow: '4px 4px 0px 0px var(--neo-shadow-color)' }}
      >
        <h2 className="font-bold text-[11px] uppercase tracking-[0.14em] mb-1 pb-3 border-b-[3px] border-on-background text-on-surface flex items-center gap-2">
          <Icon name="bolt" className="text-[16px] text-primary-container" />
          CIS Boost Formula
        </h2>
        <p className="text-[11px] text-on-surface-variant mb-4 mt-2">
          CIS Boost = Staked CLT × Duration Multiplier × 0.2, scaled by a maturity bonus that ramps from 0.5x on day one
          up to 1.0x once your full lock term has passed.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {CIS_BOOST_MULTIPLIERS.map((tier, i) => (
            <div
              key={i}
              className={`border-2 p-4 flex items-center gap-3 ${
                i >= 2
                  ? 'border-primary-container bg-primary-container/10'
                  : 'border-on-background/20 bg-surface-container'
              }`}
            >
              <Icon
                name={tier.icon}
                className={`text-[28px] flex-shrink-0 ${i >= 2 ? 'text-primary-container' : 'text-on-surface'}`}
              />
              <div>
                <p className="font-extrabold text-sm text-on-surface leading-none">
                  {tier.minCis.toLocaleString()}–{tier.maxCis.toLocaleString()} CIS
                </p>
                <p className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant mt-0.5">
                  {tier.label} · {tier.multiplier}
                </p>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-4 text-[12px] text-on-surface-variant border-l-4 border-primary-container pl-3">
          Example: {EXAMPLE_STAKE_CLT.toLocaleString()} CLT staked for 180 days earns{' '}
          {(EXAMPLE_STAKE_CLT * 1.25 * 0.5 * STAKING_FACTOR).toLocaleString()} CIS the day you stake it, ramping up to{' '}
          {(EXAMPLE_STAKE_CLT * 1.25 * 1.0 * STAKING_FACTOR).toLocaleString()} CIS once the full 180-day term matures
          ({EXAMPLE_STAKE_CLT.toLocaleString()} × 1.25 × 0.2 = {(EXAMPLE_STAKE_CLT * 1.25 * STAKING_FACTOR).toLocaleString()} CIS at full maturity).
        </p>
      </section>

      <div className="h-20 md:h-0" />
    </div>
  )
}
